import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    TextField,
    ButtonPrimary,
    skinVars,
    Text,
    IbanField,
    PhoneNumberField,
    SearchField,
    EmailField,
    useWindowSize,
} from '@telefonica/mistica';
import { t } from 'i18next';
import './index.scss';
import { useNavigate } from 'react-router-dom';
import { changeOwnershipCreateRequest, NewCustomer, SearchAddress } from '../../../../models/OwnershipChange';
import { useChangeOwnershipContext } from '../../../../context/change-ownership-context/ChangeOwnershipContext';
import { validateTextLength } from '../../../../utils/dataTransformation/validateData';
import {
    createCustomer,
    getCompleteAddressSuggestions,
    getPartialAddressSuggestions,
} from '../../../../services/OwnershipChangeService';
import useChangeOwnership from '../../../../hooks/useChangeOwnership';

const NewCustomerForm: React.FC = () => {
    const navigate = useNavigate();
    const [validForm, setValidForm] = useState(false);
    const [partialAddress, setPartialAddress] = useState('');
    const [partialAddressId, setPartialAddressId] = useState('');
    const [completeAddress, setCompleteAddress] = useState('');
    const [selectedCompleteAddressId, setSelectedCompleteAddressId] = useState('');
    const [partialAddressOptions, setPartialAddressOptions] = useState<SearchAddress[]>([]);
    const [completeAddressOptions, setCompleteAddressOptions] = useState<SearchAddress[]>([]);
    const { newCustomer, setNewCustomer, dataChangeOwnership, setCustomerId } = useChangeOwnershipContext();
    const { getChangeOwnership } = useChangeOwnership();
    const escalation = sessionStorage.getItem('escalationInfoOpen');
    const infoEscalation = JSON.parse(escalation!);
    const size = useWindowSize();

    const validationTimeoutRef = useRef<NodeJS.Timeout>();
    const partialRequestIdRef = useRef(0);
    const fullRequestIdRef = useRef(0);
    const latestPartialQueryRef = useRef('');
    const latestFullQueryRef = useRef({ address: '', addressId: '' });
    const partialSearchFieldRef = useRef<any>(null);
    const completeSearchFieldRef = useRef<any>(null);
    const partialAddressOptionsRef = useRef<SearchAddress[]>([]);
    const completeAddressOptionsRef = useRef<SearchAddress[]>([]);

    const normalizeAddress = useCallback((value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase(), []);

    const getNativeInput = useCallback((ref: React.RefObject<any>): HTMLInputElement | null => {
        const node = ref.current;
        if (!node) return null;

        if (node instanceof HTMLInputElement) return node;

        if (node instanceof HTMLElement) {
            return node.querySelector('input');
        }

        return null;
    }, []);

    useEffect(() => {
        // getChangeOwnership();
        localStorage.setItem('changeOwnershipFlow', 'newCustomer');
    }, []);

    useEffect(() => {
        partialAddressOptionsRef.current = partialAddressOptions;
    }, [partialAddressOptions]);

    useEffect(() => {
        completeAddressOptionsRef.current = completeAddressOptions;
    }, [completeAddressOptions]);

    useEffect(() => {
        const trimmedPartialAddress = partialAddress.trim();
        latestPartialQueryRef.current = trimmedPartialAddress;

        if (trimmedPartialAddress.length >= 4) {
            const exactMatch = partialAddressOptionsRef.current.find(
                (o) => o.locationFullAddress === trimmedPartialAddress,
            );
            if (exactMatch) return;
            getPartialAddresses(trimmedPartialAddress);
            return;
        }

        partialRequestIdRef.current += 1;
        setPartialAddressOptions([]);
        resetAddress();
    }, [partialAddress]);

    useEffect(() => {
        if (!partialAddressOptions.length) {
            if (partialAddressId) setPartialAddressId('');
            return;
        }

        const normalizedPartialAddress = normalizeAddress(partialAddress);
        const selectedOption = partialAddressOptions.find(
            (option) => normalizeAddress(option.locationFullAddress) === normalizedPartialAddress,
        );

        if (selectedOption) {
            if (partialAddressId !== selectedOption.id) {
                setPartialAddressId(selectedOption.id);
            }

            if (!completeAddress.trim() && newCustomer.geographicSiteId !== selectedOption.id) {
                setNewCustomer((prev) => ({ ...prev, geographicSiteId: selectedOption.id }));
            }
        } else {
            resetAddress();
        }
    }, [
        partialAddress,
        partialAddressOptions,
        partialAddressId,
        completeAddress,
        newCustomer.geographicSiteId,
        setNewCustomer,
        normalizeAddress,
    ]);

    useEffect(() => {
        const trimmedCompleteAddress = completeAddress.trim();
        latestFullQueryRef.current = { address: trimmedCompleteAddress, addressId: partialAddressId };

        if (partialAddressId && trimmedCompleteAddress.length > 0) {
            getFullAddresses(trimmedCompleteAddress, partialAddressId);
            return;
        }

        fullRequestIdRef.current += 1;
        setCompleteAddressOptions([]);
        if (newCustomer.geographicSiteId) setNewCustomer((prev) => ({ ...prev, geographicSiteId: '' }));
    }, [completeAddress, partialAddressId]);

    useEffect(() => {
        const trimmedCompleteAddress = completeAddress.trim();

        if (!trimmedCompleteAddress) {
            if (selectedCompleteAddressId) setSelectedCompleteAddressId('');
            if (newCustomer.geographicSiteId !== partialAddressId) {
                setNewCustomer((prev) => ({ ...prev, geographicSiteId: partialAddressId || '' }));
            }
            return;
        }

        if (!completeAddressOptions.length) {
            if (selectedCompleteAddressId) {
                if (newCustomer.geographicSiteId !== selectedCompleteAddressId) {
                    setNewCustomer((prev) => ({ ...prev, geographicSiteId: selectedCompleteAddressId }));
                }
            } else if (newCustomer.geographicSiteId !== partialAddressId) {
                setNewCustomer((prev) => ({ ...prev, geographicSiteId: partialAddressId || '' }));
            }
            return;
        }

        const normalizedCompleteAddress = normalizeAddress(completeAddress);
        const selectedOption = completeAddressOptions.find(
            (option) => normalizeAddress(option.locationFullAddress) === normalizedCompleteAddress,
        );

        if (selectedOption) {
            if (selectedCompleteAddressId !== selectedOption.id) {
                setSelectedCompleteAddressId(selectedOption.id);
            }
            if (newCustomer.geographicSiteId !== selectedOption.id) {
                setNewCustomer((prev) => ({ ...prev, geographicSiteId: selectedOption.id }));
            }
        } else {
            if (selectedCompleteAddressId) {
                setSelectedCompleteAddressId('');
            }
            if (newCustomer.geographicSiteId !== partialAddressId) {
                setNewCustomer((prev) => ({ ...prev, geographicSiteId: partialAddressId || '' }));
            }
        }
    }, [
        completeAddress,
        completeAddressOptions,
        newCustomer.geographicSiteId,
        partialAddressId,
        selectedCompleteAddressId,
        setNewCustomer,
        normalizeAddress,
    ]);

    const checkValidations = useCallback(() => {
        const isNameValid = validateTextLength(newCustomer.name, 0);
        const isFirstSurnameValid = validateTextLength(newCustomer.firstSurname, 0);
        const isPhoneValid = validateTextLength(newCustomer.contactPhone, 0);
        const isIbanValid = validateTextLength(newCustomer.iban, 0);
        const trimmedCompleteAddress = completeAddress.trim();
        const selectedComplete = completeAddressOptionsRef.current.find(
            (o) => normalizeAddress(o.locationFullAddress) === normalizeAddress(trimmedCompleteAddress),
        );
        const selectedPartial = partialAddressOptionsRef.current.find(
            (o) => normalizeAddress(o.locationFullAddress) === normalizeAddress(partialAddress),
        );
        const resolvedGeographicSiteId =
            selectedComplete?.id || selectedCompleteAddressId || selectedPartial?.id || partialAddressId || '';
        const effectiveGeographicSiteId = trimmedCompleteAddress
            ? newCustomer.geographicSiteId || resolvedGeographicSiteId
            : resolvedGeographicSiteId;
        const isGeographicSiteIdValid = validateTextLength(effectiveGeographicSiteId, 0);

        return isNameValid && isFirstSurnameValid && isPhoneValid && isIbanValid && isGeographicSiteIdValid;
    }, [newCustomer, completeAddress, partialAddress, partialAddressId, selectedCompleteAddressId, normalizeAddress]);

    useEffect(() => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        validationTimeoutRef.current = setTimeout(() => {
            setValidForm(checkValidations());
        }, 300);

        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, [newCustomer, checkValidations]);

    const onChangeNewCustomer = (field: keyof NewCustomer) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewCustomer((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSearchFieldFocus = (fieldRef: React.RefObject<any>) => () => {
        if (window.innerWidth <= 768) {
            setTimeout(() => {
                const input = getNativeInput(fieldRef);
                if (input) {
                    input.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
            }, 100);
        }
    };

    const getPartialAddresses = async (address: string) => {
        const currentRequestId = ++partialRequestIdRef.current;

        try {
            const response = await getPartialAddressSuggestions(address);
            const options = Array.isArray(response.data) ? response.data : [];

            if (currentRequestId === partialRequestIdRef.current && latestPartialQueryRef.current === address) {
                setPartialAddressOptions(options);
            }

            return response.data;
        } catch (error) {
            if (currentRequestId === partialRequestIdRef.current && latestPartialQueryRef.current === address) {
                setPartialAddressOptions([]);
            }
            console.error(error);
        }
    };

    const getFullAddresses = async (address: string, addressId: string) => {
        const currentRequestId = ++fullRequestIdRef.current;

        try {
            const response = await getCompleteAddressSuggestions(address, addressId);
            const options = Array.isArray(response.data) ? response.data : [];

            if (
                currentRequestId === fullRequestIdRef.current &&
                latestFullQueryRef.current.address === address &&
                latestFullQueryRef.current.addressId === addressId
            ) {
                setCompleteAddressOptions(options);
            }
        } catch (error) {
            if (
                currentRequestId === fullRequestIdRef.current &&
                latestFullQueryRef.current.address === address &&
                latestFullQueryRef.current.addressId === addressId
            ) {
                setCompleteAddressOptions([]);
            }
            console.error(error);
        }
    };

    const getPartialAddressesSuggestions = useMemo(
        () => () => partialAddressOptions.map((option) => option.locationFullAddress),
        [partialAddressOptions],
    );

    const getFullAddressesSuggestions = useMemo(
        () => () => completeAddressOptions.map((option) => option.locationFullAddress),
        [completeAddressOptions],
    );

    const resetAddress = () => {
        setPartialAddressId('');
        setCompleteAddress('');
        setSelectedCompleteAddressId('');
        setCompleteAddressOptions([]);
        if (newCustomer.geographicSiteId) setNewCustomer((prev) => ({ ...prev, geographicSiteId: '' }));
    };

    const handleButtonContinue = async () => {
        const trimmedCompleteAddress = completeAddress.trim();
        const trimmedPartialAddress = partialAddress.trim();
        const selectedComplete = completeAddressOptionsRef.current.find(
            (o) => normalizeAddress(o.locationFullAddress) === normalizeAddress(trimmedCompleteAddress),
        );
        const selectedPartial = partialAddressOptionsRef.current.find(
            (o) => normalizeAddress(o.locationFullAddress) === normalizeAddress(trimmedPartialAddress),
        );
        const completeAddressGeographicSiteId =
            selectedComplete?.id ||
            selectedCompleteAddressId ||
            (trimmedCompleteAddress && newCustomer.geographicSiteId !== partialAddressId
                ? newCustomer.geographicSiteId
                : '');
        const geographicSiteId = completeAddressGeographicSiteId || selectedPartial?.id || partialAddressId || '';

        const data: changeOwnershipCreateRequest = {
            geographicSiteId: geographicSiteId,
            contactNumber: newCustomer.contactPhone.trim(),
            contactEmail: newCustomer.email.trim(),
            signalingId: infoEscalation?.signalingId,
            name: newCustomer.name.trim(),
            firstSurname: newCustomer.firstSurname.trim(),
            secondSurname: newCustomer.secondSurname?.trim(),
        };

        try {
            const response = await createCustomer(data);
            setCustomerId(response.data?.customerId ?? '');
            navigate('/change-ownership/continue');
        } catch (error) {
            console.error(error);
        }
    };

    const content = (
        <div className='newcustomerform-page__textfield-grid'>
            <Text weight='regular' size={16} color={skinVars.colors.textPrimary}>
                {t('accountholderpersonalinfo')}
            </Text>
            <div className='newcustomerform-page__textfield'>
                <TextField
                    name='holderName'
                    label={t('holderNameLabel')}
                    autoFocus
                    maxLength={60}
                    value={newCustomer.name}
                    onChange={onChangeNewCustomer('name')}
                />
            </div>
            <div className='newcustomerform-page__textfield'>
                <TextField
                    name='holderFirstSurname'
                    label={t('holderFirstSurnameLabel')}
                    maxLength={60}
                    value={newCustomer.firstSurname}
                    onChange={onChangeNewCustomer('firstSurname')}
                />
            </div>
            <div className='newcustomerform-page__textfield'>
                <TextField
                    name='holderSecondSurname'
                    label={t('holderSecondSurnameLabel')}
                    maxLength={60}
                    value={newCustomer.secondSurname ?? ''}
                    onChange={onChangeNewCustomer('secondSurname')}
                />
            </div>
            {/* <div className='newcustomerform-page__textfield'>
                <TextField
                    name='holderDocumentType'
                    label={t('holderDocumentTypeLabel')}
                    maxLength={20}
                    value={dataChangeOwnership?.interested?.documentType}
                    disabled
                />
            </div>
            <div className='newcustomerform-page__textfield'>
                <TextField
                    name='holderNationalId'
                    label={t('holderNationalIdLabel')}
                    maxLength={20}
                    value={dataChangeOwnership?.interested?.documentNumber}
                    disabled
                />
            </div> */}
            <div className='newcustomerform-page__textfield'>
                <PhoneNumberField
                    name='holderContactPhone'
                    label={t('holderContactPhoneLabel')}
                    maxLength={20}
                    value={newCustomer.contactPhone}
                    onChangeValue={(_, rawValue) =>
                        setNewCustomer((prev) => ({
                            ...prev,
                            contactPhone: rawValue.replace(/\s+/g, ''),
                        }))
                    }
                    prefix='+34'
                />
            </div>
            <div className='newcustomerform-page__textfield'>
                <EmailField
                    name='holderEmail'
                    label={t('email')}
                    maxLength={60}
                    value={newCustomer.email}
                    onChange={onChangeNewCustomer('email')}
                />
            </div>
            <Text weight='regular' size={16} lineHeight={24} color={skinVars.colors.textPrimary}>
                {t('accountIban')}
            </Text>
            <div className='newcustomerform-page__textfield'>
                <IbanField
                    name='holderIban'
                    label={t('holderIbanLabel')}
                    maxLength={60}
                    value={newCustomer.iban}
                    onChangeValue={(_, rawValue) =>
                        setNewCustomer((prev) => ({
                            ...prev,
                            iban: rawValue.replace(/\s+/g, '').toUpperCase(),
                        }))
                    }
                />
            </div>
            <Text weight='regular' size={16} lineHeight={24} color={skinVars.colors.textPrimary}>
                {t('usualResidentialAddress')}
            </Text>
            <div className='newcustomerform-page__textfield'>
                <SearchField
                    ref={partialSearchFieldRef}
                    name='holderResidentialAddress'
                    label={t('holderResidentialAddressLabel')}
                    maxLength={60}
                    value={partialAddress}
                    onChange={(e) => setPartialAddress(e.target.value)}
                    onFocus={handleSearchFieldFocus(partialSearchFieldRef)}
                    helperText={t('holderResidentialAddressHelperText')}
                    getSuggestions={getPartialAddressesSuggestions}
                    shouldShowSuggestions={5}
                    inputMode='text'
                />
            </div>
            <Text weight='regular' size={16} lineHeight={24} color={skinVars.colors.textPrimary}>
                {t('completeAddress')}
            </Text>
            <div className='newcustomerform-page__textfield'>
                <SearchField
                    ref={completeSearchFieldRef}
                    name='holderCompleteAddress'
                    label={t('holderCompleteAddressLabel')}
                    maxLength={60}
                    value={completeAddress}
                    onChange={(e) => setCompleteAddress(e.target.value)}
                    onFocus={handleSearchFieldFocus(completeSearchFieldRef)}
                    disabled={!partialAddressId}
                    getSuggestions={getFullAddressesSuggestions}
                    inputMode='text'
                />
            </div>
            <div className='newcustomerform-page__textfield-grid__footer'>
                <ButtonPrimary
                    style={{ width: `${size.width < 1024 ? '100%' : 'auto'}` }}
                    disabled={!validForm}
                    onPress={handleButtonContinue}
                >
                    {t('continue')}
                </ButtonPrimary>
            </div>
        </div>
    );

    return (
        <div className='newcustomerform-page'>
            <div className='newcustomerform-page__content'>{content}</div>
        </div>
    );
};

export default NewCustomerForm;
